/* ============================================================
   Mighty slide-out cart — offer coordinator (gifts + discount codes)
   Runs a single SERIALIZED pipeline so gift-add and code-apply never race:
     1) ensure free gifts (when the trigger/90-day variant is in the cart)
     2) apply the right discount codes for the cart contents
     3) render: normally ONE drawer re-render from server truth — but when the
        listicle's INSTANT pre-rendered drawer is showing (data-ml-static marker),
        verify server truth against it instead and only re-render on mismatch,
        so the shopper never sees a swap (no flicker).
   Triggered on load, on cartUpdate, and via window.mightyCartSettle() (the
   listicle ATC calls this after adding). Idempotent + re-entrant-safe.

   Config from the script tag: data-gift-variants, data-gift-trigger.
   Codes from <cart-drawer data-*>: sub-codes, gift-codes, onetime-codes, gift-variant.
   ============================================================ */
(function () {
  'use strict';
  const script = document.currentScript || document.querySelector('script[src*="mighty-cart-offers"]');
  const GIFTS = (script && script.dataset.giftVariants ? script.dataset.giftVariants : '')
    .split(',').map(function (s) { return s.trim(); }).filter(Boolean);
  const TRIGGER = script && script.dataset.giftTrigger ? String(script.dataset.giftTrigger).trim() : '';
  const attempted = new Set();

  function list(v) { return (v || '').split(',').map(function (s) { return s.trim(); }).filter(Boolean); }
  function getCart() { return fetch(`${routes.cart_url}.js`).then(function (r) { return r.json(); }); }

  async function ensureGifts(cart) {
    if (!GIFTS.length || !TRIGGER) return false;
    // Shopper explicitly removed the gifts (safety valve) — don't re-add them.
    if (cart.attributes && cart.attributes._mc_gifts_off === 'true') return false;
    const ids = cart.items.map(function (i) { return String(i.variant_id); });
    if (ids.indexOf(TRIGGER) === -1) return false;
    const missing = GIFTS.filter(function (g) { return ids.indexOf(g) === -1; });
    if (!missing.length) return false;
    // /cart/update.js (NOT add.js): Shopify's web pixel auto-fires Meta AddToCart for every
    // item that goes through add.js, so gift-adds were tripling ATC on the kit (main + 2 gifts).
    // update.js inserts absent variants without that hook — the shopper's ONE real add (mlBuy's
    // add.js call) stays the only ATC. Trade-off: update.js can't set line properties, so gifts
    // no longer carry _gift — everything downstream identifies them by variant id instead
    // (cart-drawer.liquid gift_variant_ids, subscribe.js gift exclusion, staticMatches).
    const updates = {};
    missing.forEach(function (g) { updates[g] = 1; });
    await fetch('/cart/update.js', Object.assign({}, fetchConfig(), {
      body: JSON.stringify({ updates: updates }),
    }));
    return true;
  }

  async function applyCodes(cart) {
    const cd = document.querySelector('cart-drawer');
    if (!cd || !cart.items.length) return false;
    const giftVariant = (cd.dataset.giftVariant || '').trim();
    const subCodes = list(cd.dataset.subCodes);
    const giftCodes = list(cd.dataset.giftCodes);
    const oneCodes = list(cd.dataset.onetimeCodes);
    const hasSub = cart.items.some(function (i) { return i.selling_plan_allocation; });
    const hasOneTime = cart.items.some(function (i) { return !i.selling_plan_allocation && GIFTS.indexOf(String(i.variant_id)) === -1; });
    const hasGiftTrigger = giftVariant && cart.items.some(function (i) { return String(i.variant_id) === giftVariant; });
    // Additive: apply the codes for EVERY offer type present. A mixed cart (one-time + sub)
    // gets both the one-time code and the sub codes; Shopify decides which lines each is
    // eligible for. A pure cart just gets its own set.
    let want = [];
    if (hasSub) want = want.concat(subCodes);
    if (hasOneTime) want = want.concat(oneCodes);
    if (hasGiftTrigger) want = want.concat(giftCodes);
    if (!want.length) return false;
    const have = new Set([].concat(
      (cart.discount_codes || []).map(function (d) { return (d.code || '').toUpperCase(); }),
      (cart.cart_level_discount_applications || []).map(function (d) { return (d.title || '').toUpperCase(); })
    ));
    const toApply = want.filter(function (c) { return !have.has(c.toUpperCase()) && !attempted.has(c.toUpperCase()); });
    if (!toApply.length) return false;
    toApply.forEach(function (c) { attempted.add(c.toUpperCase()); });
    // Fire the codes in PARALLEL for speed (one round-trip instead of N)...
    await Promise.all(toApply.map(function (code) {
      return fetch('/discount/' + encodeURIComponent(code) + '?redirect=/cart.js', { credentials: 'same-origin' }).catch(function () {});
    }));
    // ...then VERIFY each stuck and sequentially retry any the parallel writes lost. Parallel
    // /discount calls can lose-update one another, which would silently drop a revenue-critical
    // code like WELCOME-KIT (charging for the "free" gifts). This closes that gap.
    const check = await getCart();
    const applied = new Set((check.discount_codes || []).filter(function (d) { return d.applicable; }).map(function (d) { return (d.code || '').toUpperCase(); }));
    const missing = toApply.filter(function (c) { return !applied.has(c.toUpperCase()); });
    for (let i = 0; i < missing.length; i++) {
      try { await fetch('/discount/' + encodeURIComponent(missing[i]) + '?redirect=/cart.js', { credentials: 'same-origin' }); } catch (e) {}
    }
    return true;
  }

  function refreshBubble() {
    return fetch(`${routes.cart_url}?section_id=cart-icon-bubble`).then(function (r) { return r.text(); }).then(function (txt) {
      const bd = new DOMParser().parseFromString(txt, 'text/html');
      const nb = bd.getElementById('cart-icon-bubble');
      const cb = document.getElementById('cart-icon-bubble');
      if (nb && cb) cb.innerHTML = nb.innerHTML;
    }).catch(function () {});
  }

  function refreshDrawer() {
    return Promise.all([
      fetch(`${routes.cart_url}?section_id=cart-drawer`).then(function (r) { return r.text(); }),
      refreshBubble(),
    ]).then(function (res) {
      const html = new DOMParser().parseFromString(res[0], 'text/html');
      const src = html.querySelector('.drawer__inner');
      const tgt = document.querySelector('#CartDrawer .drawer__inner');
      if (src && tgt) tgt.innerHTML = src.innerHTML;
      const sh = html.querySelector('cart-drawer');
      const lh = document.querySelector('cart-drawer');
      if (sh && lh) lh.classList.toggle('is-empty', sh.classList.contains('is-empty'));
    }).catch(function () {});
  }

  /* ---- static pre-render support (listicle instant drawer) ----
     mlBuy injects pre-rendered drawer markup carrying a hidden marker div:
       <div data-ml-static data-expect-variants="vid:qty,vid:qty" data-expect-total="14025" hidden></div>
     While that marker is present, settle() verifies the real cart against the
     expectation instead of re-rendering: on match it only copies the volatile
     cart identifiers (line index, line key) into the static markup so qty/remove
     keep working; on mismatch server truth wins and we re-render normally. */
  function staticMarker() { return document.querySelector('#CartDrawer [data-ml-static]'); }

  function staticMatches(cart, marker) {
    try {
      const expect = {};
      (marker.getAttribute('data-expect-variants') || '').split(',').forEach(function (p) {
        const kv = p.split(':'); if (kv[0]) expect[kv[0].trim()] = parseInt(kv[1] || '1', 10);
      });
      const got = {};
      cart.items.forEach(function (i) { const v = String(i.variant_id); got[v] = (got[v] || 0) + i.quantity; });
      const ev = Object.keys(expect);
      if (ev.length !== Object.keys(got).length) return false;
      for (let i = 0; i < ev.length; i++) { if (got[ev[i]] !== expect[ev[i]]) return false; }
      return parseInt(marker.getAttribute('data-expect-total') || '-1', 10) === cart.total_price;
    } catch (e) { return false; }
  }

  function syncStatic(cart) {
    const scope = document.querySelector('#CartDrawer .drawer__inner');
    if (!scope) return;
    cart.items.forEach(function (item, idx) {
      const line = idx + 1;
      const vid = String(item.variant_id);
      const input = scope.querySelector('.quantity__input[data-quantity-variant-id="' + vid + '"]');
      if (input) {
        input.setAttribute('data-index', line);
        input.id = 'Drawer-quantity-' + line;
        input.value = item.quantity;
      }
      const rm = scope.querySelector('cart-remove-button button[data-variant-id="' + vid + '"]');
      if (rm) {
        const host = rm.closest('cart-remove-button');
        host.setAttribute('data-index', line);
        host.id = 'CartDrawer-Remove-' + line;
      }
      const gift = scope.querySelector('.mc-gift-remove[data-variant-id="' + vid + '"]');
      if (gift) gift.setAttribute('data-key', item.key);
      const row = (input || gift) ? (input || gift).closest('.cart-item') : null;
      if (row) {
        row.id = 'CartDrawer-Item-' + line;
        const err = row.querySelector('.cart-item__error');
        if (err) err.id = 'CartDrawer-LineItemError-' + line;
      }
    });
  }

  async function settle(fresh, clearUpgrade) {
    try {
      let cart = await getCart();
      // The "You're subscribed & saving — smart move!" banner must ONLY follow an actual
      // OTP→subscription upgrade (which sets _mc_upgraded). It's cleared here on a direct
      // add (listicle ATC) or page load, so the flag can't persist and make the banner
      // show for every later subscription. The upgrade's own cartUpdate re-settle passes
      // clearUpgrade=falsy, so the banner survives the upgrade that earned it.
      if (clearUpgrade && cart.attributes && cart.attributes._mc_upgraded === 'true') {
        await fetch('/cart/update.js', Object.assign({}, fetchConfig(), { body: JSON.stringify({ attributes: { _mc_upgraded: '' } }) }));
        cart = await getCart();
      }
      // Clear a stale gift-decline flag on a fresh offer add (fresh=true), on page load, or once
      // the trigger has left the cart — so a one-time "remove gift" can never permanently suppress
      // the free gifts. Without this, the flag sticks on the cart and gifts stop auto-adding.
      const hasTrigger = TRIGGER && cart.items.some(function (i) { return String(i.variant_id) === TRIGGER; });
      if ((fresh || !hasTrigger) && cart.attributes && cart.attributes._mc_gifts_off === 'true') {
        await fetch('/cart/update.js', Object.assign({}, fetchConfig(), { body: JSON.stringify({ attributes: { _mc_gifts_off: '' } }) }));
        cart = await getCart();
      }
      if (!cart.items || !cart.items.length) return true;
      // Gifts + codes both derive from the current main items, so we can add gifts and apply
      // codes off the same cart snapshot (gift-add doesn't change sub/one-time/trigger state).
      await ensureGifts(cart);
      await applyCodes(cart);
      const marker = staticMarker();
      if (marker) {
        const finalCart = await getCart();
        if (staticMatches(finalCart, marker)) {
          syncStatic(finalCart);
          await refreshBubble();
        } else {
          // Server truth disagrees with the pre-render (pre-existing cart items, code didn't
          // stick, price drift) — replace the static content with reality. Rare by design.
          try { if (window.posthog && posthog.capture) posthog.capture('lp_drawer_static_mismatch', { page: '7-ways-mighty' }); } catch (e) {}
          await refreshDrawer();
        }
      } else {
        await refreshDrawer();
      }
      return true;
    } catch (e) {
      console.error('[mighty-cart] settle failed', e);
      return false;
    }
  }

  // Every settle entry point routes through ONE promise chain, so runs never overlap and each
  // caller awaits its OWN completed run. That matters for the listicle checkout gate: it awaits
  // mightyCartSettle()'s result, and an early/shared resolve would let a shopper through to
  // checkout before the discount codes are committed. (Replaces the old busy/queued flags,
  // which returned undefined to colliding callers.)
  let lastSettle = Promise.resolve(true);
  function queueSettle(fresh, clearUpgrade) {
    const run = function () { return settle(fresh, clearUpgrade); };
    lastSettle = lastSettle.then(run, run);
    return lastSettle;
  }

  // Page load re-enables gifts (fresh=true) and clears a stale upgrade flag (clearUpgrade=true)
  // so both self-heal from a prior session.
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { queueSettle(true, true); });
  else queueSettle(true, true);

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (ev) {
      if (ev && ev.source === 'mighty-offers') return;
      queueSettle();
    });
  }

  // The listicle ATC (mlBuy) calls this right after adding the offer. A direct add is a new
  // purchase intent (not an upgrade), so clear any stale _mc_upgraded flag (clearUpgrade=true).
  window.mightyCartSettle = function (fresh) { return queueSettle(fresh, true); };

  // Gift removal (safety valve): mark gifts declined so ensureGifts won't re-add them on the
  // next settle, then drop the line and re-render. Delegated so it survives drawer re-renders.
  document.addEventListener('click', function (e) {
    const btn = e.target && e.target.closest ? e.target.closest('.mc-gift-remove') : null;
    if (!btn) return;
    e.preventDefault();
    const key = btn.getAttribute('data-key');
    if (!key) return;
    btn.disabled = true;
    fetch('/cart/update.js', Object.assign({}, fetchConfig(), { body: JSON.stringify({ attributes: { _mc_gifts_off: 'true' } }) }))
      .then(function () { return fetch('/cart/change.js', Object.assign({}, fetchConfig(), { body: JSON.stringify({ id: key, quantity: 0 }) })); })
      .then(function () { return refreshDrawer(); })
      .catch(function () { btn.disabled = false; });
  });
})();
/* build 2026-07-16T2 pixel-fix */
